import type { User } from './user'

export interface WorkflowFile {
  draftFsi?: {
    id: number
    name: string
    type: string
  }
  targetFsi?: {
    id: number
    name: string
    type: string
  }
  sourceFsi?: {
    id: number
    name: string
    type: string
  }
}

export interface TaskTeamItem {
  userId: number
  user?: User
}

export interface TaskTeamStep {
  taskTeamItems: TaskTeamItem[]
  processingType?: string
  nofAllValue?: number
  rejectionReview?: boolean
  taskTeamStepLabel?: { text: string }
  // Computed properties added during workflow assembly
  insufficientNOOfApprovers?: boolean
  activeStep?: boolean
  countOfDone?: number
}

export interface WorkflowStep {
  type: string
  taskTeamSteps: TaskTeamStep[]
}

export interface WorkflowRequestType {
  initialApprovers?: {
    type: string
    steps?: unknown[]
  }
  collaborators?: {
    type: string
    steps?: unknown[]
  }
  approvers?: {
    type: string
    steps?: unknown[]
  }
  reviewers?: {
    type: string
    steps?: unknown[]
  }
}

export interface WorkflowRequest {
  collaborators: unknown[] | null
  initialApprovers: unknown[] | null
  approvers: unknown[] | null
  reviewers: unknown[] | null
  justificationTextId: number | null
  customJustificationText: string | null
  description: string
  fileName: string
  documentTypeTemplateId: number | null
  parentFolderId: number
  workflowType: string
  targetFsiId?: number
  fileId?: number
  fileIds?: number[]
  sourceFiles?: unknown[]
  obsoleteSourceDocumentUponApproval?: boolean
  expirationDate?: number | null
  isAmendmentAddition?: boolean
  [key: string]: unknown
}

export interface Workflow {
  id: number
  workflowNo: string
  warning?: string
  workflows?: Array<{ workflowNo: string }>
  workflowSteps: WorkflowStep[]
  files: WorkflowFile[]
}

export interface TaskFlags {
  canChangeProperties: boolean
  [key: string]: boolean
}

export interface Task {
  id: string
  workflowId: number
  workflowStepType: string
  workflowDraftFolderId: number
  files: WorkflowFile[]
  flags: TaskFlags
}

export interface TaskResponse {
  elements: Task[]
  totalElements: number
}

export const WORKFLOW_TYPES = {
  NEW: 'NEW',
  CHANGE: 'CHANGE',
  FILE_ARTIFACT: 'FILE_ARTIFACT',
  RECLASSIFY_DOCUMENT: 'RECLASSIFY_DOCUMENT',
  EOS_DOWNLOAD_AND_ACKNOWLEDGE: 'EOS_DOWNLOAD_AND_ACKNOWLEDGE'
} as const

export type WorkflowType = (typeof WORKFLOW_TYPES)[keyof typeof WORKFLOW_TYPES]

export const WORKFLOW_STEP_TYPES = {
  AUTHORIZE_PLACEHOLDER_CREATION: 'AUTHORIZE_PLACEHOLDER_CREATION',
  AUTHORIZE_REFERENCE_CREATION: 'AUTHORIZE_REFERENCE_CREATION',
  AUTHORIZE_MIGRATION: 'AUTHORIZE_MIGRATION',
  AUTHORIZE_FILL_PLACEHOLDER: 'AUTHORIZE_FILL_PLACEHOLDER',
  AUTHORIZE_RECLASSIFICATION: 'AUTHORIZE_RECLASSIFICATION',
  AUTHORIZE_CHANGE_REQUEST: 'AUTHORIZE_CHANGE_REQUEST',
  APPROVE_OBSOLETE: 'APPROVE_OBSOLETE',
  REVISE_EDIT: 'REVISE_EDIT',
  REVISE_EDIT_MULTIFILE: 'REVISE_EDIT_MULTIFILE',
  REVISE_EDIT_EOS: 'REVISE_EDIT_EOS',
  REVISE_EDIT_MIGRATION: 'REVISE_EDIT_MIGRATION',
  REVISE_EDIT_RECLASSIFICATION: 'REVISE_EDIT_RECLASSIFICATION',
  REVISE_EDIT_FILE_ARTIFACT: 'REVISE_EDIT_FILE_ARTIFACT',
  APPROVE_REFERENCE_CREATION: 'APPROVE_REFERENCE_CREATION',
  APPROVE_PLACEHOLDER_CREATION: 'APPROVE_PLACEHOLDER_CREATION',
  APPROVE_OBSOLETING_REFERENCE: 'APPROVE_OBSOLETING_REFERENCE',
  REVIEW: 'REVIEW',
  REVIEW_MULTIFILE: 'REVIEW_MULTIFILE',
  REVIEW_EOS: 'REVIEW_EOS',
  REVIEW_RECLASSIFICATION: 'REVIEW_RECLASSIFICATION',
  REVIEW_FILE_ARTIFACT: 'REVIEW_FILE_ARTIFACT',
  APPROVE_MULTIFILE: 'APPROVE_MULTIFILE',
  APPROVE_MIGRATION: 'APPROVE_MIGRATION',
  APPROVE: 'APPROVE',
  APPROVE_EOS: 'APPROVE_EOS',
  APPROVE_RECLASSIFICATION: 'APPROVE_RECLASSIFICATION',
  APPROVE_FILE_ARTIFACT: 'APPROVE_FILE_ARTIFACT'
} as const

export type WorkflowStepType = (typeof WORKFLOW_STEP_TYPES)[keyof typeof WORKFLOW_STEP_TYPES]

export const WF_STEP_TYPE = {
  NOT_DEFINED: 'NOT_DEFINED',
  USE_INITIATOR: 'USE_INITIATOR',
  SETUP_WITH_WORKFLOW: 'SETUP_WITH_WORKFLOW',
  USE_PREDEFINED: 'USE_PREDEFINED'
}
export const WF_STEP_PROCESSING_TYPE = {
  SERIAL: 'SERIAL',
  PARALLEL: 'PARALLEL',
  N_OF_ALL: 'N_OF_ALL'
}

export const RA_ACTION_TYPE = {
  FOR_NOBODY: 'FOR_NOBODY',
  FOR_NEW_USERS_ONLY: 'FOR_NEW_USERS_ONLY',
  FOR_ALL_USERS: 'FOR_ALL_USERS'
}

export const APPROVAL_ACTION_TYPE = {
  NOTIFY: 'NOTIFY',
  NOTIFY_EXTERNAL_SYSTEM: 'NOTIFY_EXTERNAL',
  SET_PROPERTY: 'SET_PROPERTY',
  COPY: 'COPY',
  MOVE: 'MOVE'
}
